import React, { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { PageHeader, PageBody, fmtMoney } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Plus, Trash, UserCircleGear, ArrowsClockwise } from "@phosphor-icons/react";

export default function SalesDataPage() {
  const { activeCompany, formatApiError } = useAuth();
  const [tab, setTab] = useState("team");
  const [salesmen, setSalesmen] = useState([]);
  const [accruals, setAccruals] = useState([]);
  const [scheduler, setScheduler] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", commission_pct: "" });
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));

  const load = async () => {
    if (!activeCompany) return;
    const [s, a, sch] = await Promise.all([
      api.get(`/companies/${activeCompany.id}/salesmen`),
      api.get(`/companies/${activeCompany.id}/commissions`),
      api.get(`/companies/${activeCompany.id}/commission-scheduler`),
    ]);
    setSalesmen(s.data);
    setAccruals(a.data);
    setScheduler(sch.data);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCompany?.id]);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/companies/${activeCompany.id}/salesmen`, {
        ...form, commission_pct: parseFloat(form.commission_pct) || 0,
      });
      toast.success("Salesman added");
      setOpen(false);
      setForm({ name: "", email: "", commission_pct: "" });
      load();
    } catch (e2) {
      toast.error(formatApiError(e2.response?.data?.detail) || e2.message);
    }
  };

  const updateCommission = async (sid, pct) => {
    try {
      await api.patch(`/companies/${activeCompany.id}/salesmen/${sid}`, {
        commission_pct: parseFloat(pct) || 0,
      });
      load();
    } catch (e2) {
      toast.error(formatApiError(e2.response?.data?.detail) || e2.message);
    }
  };

  const remove = async (sid) => {
    if (!window.confirm("Remove salesman?")) return;
    try {
      const { data } = await api.delete(`/companies/${activeCompany.id}/salesmen/${sid}`);
      toast.success(data.deactivated ? "Deactivated (has invoices)" : "Removed");
      load();
    } catch (e2) {
      toast.error(formatApiError(e2.response?.data?.detail) || e2.message);
    }
  };

  const accrue = async () => {
    try {
      const { data } = await api.post(`/companies/${activeCompany.id}/commissions/accrue`, { month });
      toast.success(`Accrued ${fmtMoney(data.total, activeCompany.currency)} for ${data.month}`);
      load();
    } catch (e2) {
      toast.error(formatApiError(e2.response?.data?.detail) || e2.message);
    }
  };

  const reverse = async (id) => {
    if (!window.confirm("Reverse this month's commission accrual?")) return;
    await api.delete(`/companies/${activeCompany.id}/commissions/${id}`);
    toast.success("Reversed");
    load();
  };

  const saveScheduler = async (enabled, day_of_month) => {
    try {
      const { data } = await api.patch(
        `/companies/${activeCompany.id}/commission-scheduler`,
        { enabled, day_of_month: day_of_month || scheduler?.day_of_month || 1 }
      );
      setScheduler(data);
      toast.success(enabled ? "Auto-accrual enabled" : "Auto-accrual disabled");
    } catch (e2) {
      toast.error(formatApiError(e2.response?.data?.detail) || e2.message);
    }
  };

  if (!activeCompany) return <PageBody><div className="overline">Select a company</div></PageBody>;

  return (
    <>
      <PageHeader
        eyebrow="Sales"
        title="Sales Data"
        description="Manage your sales team and their commission rates. Post monthly commission accruals as journal entries."
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="new-salesman-btn" className="rounded-sm h-9 font-mono uppercase tracking-wider text-xs">
                <Plus size={14} className="mr-2" /> Add Salesman
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-sm">
              <DialogHeader>
                <DialogTitle className="font-serif-display text-2xl">New Salesman</DialogTitle>
              </DialogHeader>
              <form onSubmit={create} className="space-y-3">
                <div>
                  <Label className="text-xs uppercase tracking-widest">Name</Label>
                  <Input
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    required
                    data-testid="salesman-name"
                    className="rounded-sm font-mono"
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest">Email (optional)</Label>
                  <Input
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="rounded-sm font-mono"
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest">Commission %</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    max="100"
                    value={form.commission_pct}
                    onChange={(e) => setForm({ ...form, commission_pct: e.target.value })}
                    required
                    data-testid="salesman-pct"
                    className="rounded-sm font-mono text-right"
                  />
                </div>
                <DialogFooter>
                  <Button type="submit" className="rounded-sm font-mono uppercase tracking-wider text-xs">
                    Add
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        }
      />
      <PageBody>
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="rounded-sm bg-muted">
            <TabsTrigger value="team" className="rounded-sm font-mono uppercase text-xs">Sales Team</TabsTrigger>
            <TabsTrigger value="accruals" className="rounded-sm font-mono uppercase text-xs">Monthly Accruals</TabsTrigger>
          </TabsList>

          <TabsContent value="team" className="mt-4">
            <div className="border border-border bg-card">
              <table className="w-full text-sm">
                <thead className="border-b border-border bg-muted/40">
                  <tr className="text-left">
                    <Th>Name</Th>
                    <Th>Email</Th>
                    <Th className="text-right">Commission %</Th>
                    <Th>Status</Th>
                    <Th className="text-right pr-4"></Th>
                  </tr>
                </thead>
                <tbody>
                  {salesmen.map((s) => (
                    <tr key={s.id} data-testid={`salesman-row-${s.id}`} className="border-b border-border last:border-b-0">
                      <Td className="font-mono flex items-center gap-2">
                        <UserCircleGear size={14} className="text-muted-foreground" />
                        {s.name}
                      </Td>
                      <Td className="text-muted-foreground">{s.email || "—"}</Td>
                      <Td className="text-right">
                        <Input
                          type="number"
                          step="0.01"
                          defaultValue={s.commission_pct}
                          onBlur={(e) => {
                            if (parseFloat(e.target.value) !== s.commission_pct) {
                              updateCommission(s.id, e.target.value);
                            }
                          }}
                          data-testid={`salesman-pct-${s.id}`}
                          className="rounded-sm font-mono text-right w-24 ml-auto"
                        />
                      </Td>
                      <Td>
                        <span className={`tag-badge ${s.active === false ? "text-muted-foreground" : "text-primary"}`}>
                          {s.active === false ? "Inactive" : "Active"}
                        </span>
                      </Td>
                      <Td className="text-right pr-4">
                        <Button variant="ghost" size="sm" onClick={() => remove(s.id)} className="rounded-sm h-8 text-destructive">
                          <Trash size={14} />
                        </Button>
                      </Td>
                    </tr>
                  ))}
                  {salesmen.length === 0 && (
                    <tr>
                      <td colSpan={5} className="text-center py-8 text-muted-foreground text-sm">
                        No salesmen yet. Add one to assign to invoices.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="accruals" className="mt-4 space-y-4">
            <div className="border border-border bg-card p-6">
              <div className="flex items-start justify-between gap-6">
                <div>
                  <div className="overline mb-2">Auto-Accrual</div>
                  <p className="text-sm text-muted-foreground max-w-xl">
                    Automatically post the prior-month commission accrual on the
                    <span className="font-mono mx-1">{scheduler?.day_of_month || 1}</span>
                    of each month. LedgerLoom runs the job hourly and posts only once per month.
                  </p>
                  {scheduler?.last_month_accrued && (
                    <div className="text-xs text-muted-foreground mt-2 num">
                      Last auto-accrual: <span className="font-medium">{scheduler.last_month_accrued}</span>
                      {scheduler.last_run_at && ` at ${scheduler.last_run_at.slice(0, 16).replace("T", " ")}`}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  <div>
                    <Label className="text-xs uppercase tracking-widest">Day of Month</Label>
                    <Input
                      type="number"
                      min="1"
                      max="28"
                      value={scheduler?.day_of_month || 1}
                      onChange={(e) => setScheduler({ ...(scheduler || {}), day_of_month: parseInt(e.target.value) || 1 })}
                      onBlur={(e) => saveScheduler(scheduler?.enabled || false, parseInt(e.target.value) || 1)}
                      data-testid="scheduler-day"
                      className="rounded-sm font-mono text-right w-20 mt-1"
                    />
                  </div>
                  <Button
                    onClick={() => saveScheduler(!scheduler?.enabled, scheduler?.day_of_month)}
                    variant={scheduler?.enabled ? "default" : "outline"}
                    data-testid="scheduler-toggle"
                    className="rounded-sm h-10 font-mono uppercase tracking-wider text-xs"
                  >
                    {scheduler?.enabled ? "Enabled · Click to disable" : "Enable auto-accrual"}
                  </Button>
                </div>
              </div>
            </div>

            <div className="border border-border bg-card p-6">
              <div className="overline mb-2">Post Monthly Commission Accrual</div>
              <p className="text-sm text-muted-foreground mb-4 max-w-2xl">
                Calculates each salesman's commission based on their AR invoices in the selected month and posts a single journal:
                {" "}
                <span className="font-mono text-xs">Dr Sales Commission Expense · Cr Accrued Sales Commissions</span>.
              </p>
              <div className="flex items-end gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-widest">Month</Label>
                  <Input
                    type="month"
                    value={month}
                    onChange={(e) => setMonth(e.target.value)}
                    data-testid="accrue-month"
                    className="rounded-sm font-mono w-40"
                  />
                </div>
                <Button
                  onClick={accrue}
                  data-testid="accrue-btn"
                  className="rounded-sm h-10 font-mono uppercase tracking-wider text-xs"
                >
                  Post Accrual
                </Button>
              </div>
            </div>

            <div className="border border-border bg-card">
              <div className="overline p-4 border-b border-border">History</div>
              <table className="w-full text-sm">
                <thead className="border-b border-border bg-muted/40">
                  <tr className="text-left">
                    <Th>Month</Th>
                    <Th>Period</Th>
                    <Th>Salesmen</Th>
                    <Th className="text-right">Total Commission</Th>
                    <Th className="text-right pr-4"></Th>
                  </tr>
                </thead>
                <tbody>
                  {accruals.map((a) => (
                    <tr key={a.id} className="border-b border-border last:border-b-0">
                      <Td className="num font-medium">{a.month}</Td>
                      <Td className="text-xs text-muted-foreground num">{a.period_start} → {a.period_end}</Td>
                      <Td>
                        <div className="flex flex-wrap gap-1">
                          {a.breakdown.map((b, i) => (
                            <span key={i} className="tag-badge">
                              {b.name}: {fmtMoney(b.commission, activeCompany.currency)}
                            </span>
                          ))}
                        </div>
                      </Td>
                      <Td className="text-right num font-medium">
                        {fmtMoney(a.total, activeCompany.currency)}
                      </Td>
                      <Td className="text-right pr-4">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => reverse(a.id)}
                          className="rounded-sm h-8 text-destructive font-mono text-xs uppercase"
                        >
                          <ArrowsClockwise size={12} className="mr-1" /> Reverse
                        </Button>
                      </Td>
                    </tr>
                  ))}
                  {accruals.length === 0 && (
                    <tr>
                      <td colSpan={5} className="text-center py-8 text-muted-foreground text-sm">
                        No accruals yet.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </TabsContent>
        </Tabs>
      </PageBody>
    </>
  );
}

const Th = ({ children, className = "" }) => (
  <th className={`px-4 py-3 overline ${className}`}>{children}</th>
);
const Td = ({ children, className = "" }) => (
  <td className={`px-4 py-3 ${className}`}>{children}</td>
);
